import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import PhotoUpload from "@/components/common/PhotoUpload";
import DataTable from "@/components/common/DataTable";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { X, Plus, SealCheck } from "@phosphor-icons/react";

const initial = {
  do_id: "", do_no: "", source_tank: "", target_qty: 0,
  operator: "", supervisor: "", notes: "",
  photo_position: "", photo_perintah: "",
  photo_nozzle: "", photo_during: "",
  photo_finished: "", photo_do: "",
  seals: [],
  start_at: "",
};

export default function LoadingProcessPage() {
  const [dos, setDos] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [form, setForm] = useState(initial);
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [newSeal, setNewSeal] = useState("");
  const [posTab, setPosTab] = useState("position");
  const [finTab, setFinTab] = useState("finished");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const load = () => {
    api.get("/delivery").then((r) => setDos(r.data)).catch(() => {});
    api.get("/master/tanks").then((r) => setTanks(r.data)).catch(() => {});
    api.get("/loading").then((r) => setRows(r.data)).catch(() => {});
  };
  useAutoRefresh(load);

  // Hanya tampilkan DO yang sudah melewati Timbang Masuk oleh security
  // (status "weighed_in") atau sedang dalam proses loading.
  // Kendaraan yang belum timbang tidak muncul di layar pengawas — mencegah
  // input Loading pada kendaraan yang belum melintas.
  const eligible = dos.filter((d) => d.approval_status === "approved" &&
    ["weighed_in", "loading"].includes(d.status));

  const addSeal = () => {
    const v = newSeal.trim();
    if (!v) return;
    if (form.seals.length >= 10) return toast.error("Maksimal 10 segel");
    if (form.seals.includes(v)) return toast.error("Nomor segel sudah ada");
    setForm((f) => ({ ...f, seals: [...f.seals, v] }));
    setNewSeal("");
  };

  const removeSeal = (s) => setForm((f) => ({ ...f, seals: f.seals.filter((x) => x !== s) }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/loading", { ...form, target_qty: parseFloat(form.target_qty) || 0 });
      toast.success("Proses loading tercatat");
      setForm(initial); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <AppShell title="Proses Loading" subtitle="Hanya kendaraan yang sudah timbang masuk yang muncul di sini. Isi bukti foto, segel, dan eksekusi loading.">
      <form onSubmit={submit} data-testid="loading-form" className="rounded-xl bg-white border border-slate-200 p-4 sm:p-6 shadow-sm space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Pilih DO (sudah timbang masuk)" required>
            <Select value={form.do_id} onValueChange={(id) => {
              const d = eligible.find((x) => x.id === id);
              setForm((f) => ({ ...f, do_id: id, do_no: d?.do_no || "", target_qty: d?.target_qty || 0 }));
            }}>
              <SelectTrigger data-testid="loading-do" className="h-10"><SelectValue placeholder={eligible.length ? "Pilih DO" : "Belum ada kendaraan yang sudah timbang masuk"} /></SelectTrigger>
              <SelectContent>
                {eligible.map((d) => <SelectItem key={d.id} value={d.id}>{d.do_no} · {d.customer}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tank Sumber" required>
            <Select value={form.source_tank} onValueChange={set("source_tank")}>
              <SelectTrigger data-testid="loading-tank" className="h-10"><SelectValue placeholder="Pilih tank" /></SelectTrigger>
              <SelectContent>
                {tanks.map((t) => <SelectItem key={t.id} value={t.code}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Target Quantity (kg)">
            <Input data-testid="loading-qty" type="number" step="0.01" value={form.target_qty} onChange={set("target_qty")} className="h-10 font-mono" />
          </Field>
          <Field label="Operator Loading" required><Input data-testid="loading-operator" value={form.operator} onChange={set("operator")} required /></Field>
          <Field label="Pengawas / Supervisor"><Input data-testid="loading-supervisor" value={form.supervisor} onChange={set("supervisor")} /></Field>
          <Field label="Waktu Mulai"><Input data-testid="loading-start" type="datetime-local" value={form.start_at} onChange={set("start_at")} className="h-10" /></Field>
        </div>

        <Field label="Catatan Loading"><Textarea data-testid="loading-notes" rows={2} value={form.notes} onChange={set("notes")} /></Field>

        {/* Seals */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600 flex items-center gap-2">
              <SealCheck size={14} weight="duotone" /> Nomor Segel (max 10 pcs)
            </label>
            <span className="text-[10px] font-mono text-slate-500">{form.seals.length}/10</span>
          </div>
          <div className="flex gap-2 mb-2">
            <Input
              data-testid="loading-seal-input"
              value={newSeal} onChange={(e) => setNewSeal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSeal(); } }}
              placeholder="Ketik nomor segel lalu Enter atau klik +"
              className="h-10 font-mono"
              disabled={form.seals.length >= 10}
            />
            <Button type="button" onClick={addSeal} data-testid="loading-seal-add"
              disabled={form.seals.length >= 10}
              className="bg-sky-600 hover:bg-sky-700 text-white h-10 px-4"><Plus size={16} weight="bold" /></Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {form.seals.map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-100 text-sky-900 text-[12px] font-mono border border-sky-200">
                <SealCheck size={11} weight="fill" /> {s}
                <button type="button" onClick={() => removeSeal(s)} className="hover:text-red-600" data-testid={`loading-seal-rm-${s}`}>
                  <X size={11} weight="bold" />
                </button>
              </span>
            ))}
            {form.seals.length === 0 && <span className="text-[11px] text-slate-400 italic">Belum ada segel ditambahkan.</span>}
          </div>
        </div>

        {/* Photos */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-800">Bukti Foto Wajib</h3>
            <span className="text-[10px] text-red-600 font-mono uppercase tracking-wider">4 foto wajib</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Slot 1: Position OR Surat Perintah */}
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/50">
              <Tabs value={posTab} onValueChange={setPosTab}>
                <TabsList className="w-full grid grid-cols-2 h-9 mb-3">
                  <TabsTrigger value="position" data-testid="tab-position" className="text-[11px] data-[state=active]:bg-sky-600 data-[state=active]:text-white">Posisi Truck</TabsTrigger>
                  <TabsTrigger value="perintah" data-testid="tab-perintah" className="text-[11px] data-[state=active]:bg-sky-600 data-[state=active]:text-white">Surat Perintah</TabsTrigger>
                </TabsList>
                <TabsContent value="position" className="mt-0"><PhotoUpload label="Foto Posisi Truck" required value={form.photo_position} onChange={set("photo_position")} testid="loading-photo-position" /></TabsContent>
                <TabsContent value="perintah" className="mt-0"><PhotoUpload label="Foto Surat Perintah Loading" required value={form.photo_perintah} onChange={set("photo_perintah")} testid="loading-photo-perintah" /></TabsContent>
              </Tabs>
            </div>

            <PhotoUpload label="Foto Nozzle / Valve" required value={form.photo_nozzle} onChange={set("photo_nozzle")} testid="loading-photo-nozzle" />
            <PhotoUpload label="Foto Selama Pengisian" required value={form.photo_during} onChange={set("photo_during")} testid="loading-photo-during" />

            {/* Slot 4: Finished OR Surat Jalan / DO */}
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/50">
              <Tabs value={finTab} onValueChange={setFinTab}>
                <TabsList className="w-full grid grid-cols-2 h-9 mb-3">
                  <TabsTrigger value="finished" data-testid="tab-finished" className="text-[11px] data-[state=active]:bg-sky-600 data-[state=active]:text-white">Akhir Loading</TabsTrigger>
                  <TabsTrigger value="do" data-testid="tab-do" className="text-[11px] data-[state=active]:bg-sky-600 data-[state=active]:text-white">Surat Jalan / DO</TabsTrigger>
                </TabsList>
                <TabsContent value="finished" className="mt-0"><PhotoUpload label="Foto Akhir Loading" required value={form.photo_finished} onChange={set("photo_finished")} testid="loading-photo-finished" /></TabsContent>
                <TabsContent value="do" className="mt-0"><PhotoUpload label="Foto Surat Jalan / DO" required value={form.photo_do} onChange={set("photo_do")} testid="loading-photo-do" /></TabsContent>
              </Tabs>
            </div>
          </div>
          <p className="text-[10.5px] text-slate-500 mt-3 leading-relaxed">
            <b>Info:</b> Foto Posisi Truck bisa digantikan dengan Foto Surat Perintah Loading. Foto Akhir Loading bisa digantikan dengan Foto Surat Jalan / DO.
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <Button type="submit" data-testid="loading-submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-6">
            {submitting ? "Memproses…" : "Selesaikan Loading"}
          </Button>
        </div>
      </form>

      <div className="mt-8">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-800 mb-3">Riwayat Loading</h3>
        <DataTable
          testid="loading-table"
          rows={rows}
          columns={[
            { key: "do_no", label: "No. DO", render: (r) => <span className="font-mono font-bold">{r.do_no}</span> },
            { key: "source_tank", label: "Tank" },
            { key: "target_qty", label: "Target", render: (r) => <span className="font-mono">{(r.target_qty || 0).toLocaleString("id-ID")}</span> },
            { key: "seals", label: "Segel", render: (r) => <span className="font-mono text-[11px]">{(r.seals || []).length} pcs</span> },
            { key: "operator", label: "Operator" },
            { key: "supervisor", label: "Pengawas" },
            { key: "created_at", label: "Waktu", render: (r) => <span className="font-mono text-xs">{new Date(r.created_at).toLocaleString("id-ID")}</span> },
          ]}
        />
      </div>
    </AppShell>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
